/**
 * Section 12d — Smart Money (copy-the-top-trader signals).
 *
 * Follows top on-chain (Hyperliquid) traders and raises suggest-only signals when one
 * opens a position mappable to a Delta perp, with a freshness gate + consensus. Also
 * ingests UNVERIFIED NSE social calls (paste / forward) into the same feed. Documents
 * the sources, the pipeline, the tabs, and the honest data-availability caveats.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const LI = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}><ListItemText primary={children} /></ListItem>
);

const Section_smart_money: GuideSection = {
  id: 'smart-money',
  label: 'Smart Money (copy signals)',
  group: 'pages',
  icon: <GroupsIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Smart Money · copy signals</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Follows top traders and alerts you when one opens a position <strong>you can trade</strong> — so you can
        copy the move. You <strong>validate on the chart, then place the trade yourself</strong>. Open it at{' '}
        <OpenInApp to="/smart-money" label="Smart Money" />. It is <strong>suggest-only</strong> — nothing auto-executes.
      </Typography>

      <GuideCallout kind="info" title="Off by default — you opt in">
        The engine does nothing until you toggle it <strong>ON</strong> (top-right) and hit <strong>Scan now</strong>.
        When ON it polls your followed traders every few minutes (well within a 5–15 min swing window).
      </GuideCallout>

      <H id="sources">Where the signals come from</H>
      <H3>Crypto — Hyperliquid (real, verifiable)</H3>
      <P>
        Hyperliquid is an on-chain perpetuals exchange, so <strong>every trader's positions are public and
        verifiable</strong> — no self-reported claims. The engine reads the ranked leaderboard and each followed
        wallet's on-chain fills, and maps their coin to the matching <strong>Delta</strong> perp (e.g. BTC → BTCUSD,
        kPEPE → 1000PEPEUSD). Coins with no Delta market are hidden.
      </P>
      <H3>NSE — unverified social calls</H3>
      <P>
        There is <strong>no free, verified feed of top-trader positions for Indian equities</strong> (unlike
        on-chain crypto). So NSE works differently: you (or a Telegram forwarding rule / script) feed in
        <strong> tips as text</strong>, which are parsed into signals. These are <strong>unverified</strong> — treat
        them as ideas to validate, and let each source's hit-rate build over time.
      </P>
      <GuideCallout kind="warning" title="Why not Binance / OKX copy-trading?">
        Binance has no official leaderboard API (only brittle scrapers). OKX's public API exposes lead-trader
        rankings and <em>closed</em> history but <strong>masks their live open positions</strong> — so it can't power
        a "just entered" signal for free. On-chain (Hyperliquid) is the one source with genuinely public, real-time,
        verifiable positions, which is why it's the crypto engine.
      </GuideCallout>

      <H id="how">How it works</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Follow</strong> — auto-follows the top-N Hyperliquid traders by a <em>swing-biased quality
          score</em> (account size, week/month consistency, low churn — deliberately <strong>not</strong> raw PnL,
          which is dominated by survivorship and high-leverage gamblers). Add any wallet manually in Traders.</></LI>
        <LI><><strong>Detect</strong> — reads each followed trader's fills and flags a fresh <strong>open</strong>{' '}
          (or a flip). The first poll after you follow is a <strong>baseline</strong>, so only positions taken{' '}
          <em>after</em> you follow are copied — no backfill spam.</></LI>
        <LI><><strong>Freshness gate</strong> — compares the trader's entry price to the live price. If price has
          already run past your threshold the signal is marked <strong>"chased"</strong> and won't alert, so you
          don't enter late.</></LI>
        <LI><><strong>Consensus</strong> — when several followed traders hold the same coin/side, the signal shows a
          consensus badge (stronger conviction).</></LI>
        <LI><><strong>Validate &amp; Trade</strong> — one click opens the symbol's chart to confirm the setup; you
          place the trade yourself.</></LI>
      </List>

      <H id="tabs">The tabs</H>
      <H3>Signals</H3>
      <P>
        The live feed, newest first. Each card shows the direction (LONG/SHORT), symbol, the trader (handle · rank),
        their entry vs the current price with a <strong>freshness gauge</strong> ("better −0.2%" / "chased +1.4%"),
        the size as a % of their book, a consensus badge, and how long ago they entered. Filter by status
        (Active / New / All) and source (Crypto / NSE). <strong>+ NSE call</strong> opens the paste box. Each card
        has <strong>Validate &amp; Trade on chart</strong>, plus Mark-traded / Dismiss.
      </P>
      <H3>Traders</H3>
      <P>
        Your followed set and the ranked <strong>Discover</strong> pool (account value, month/all-time ROI, quality
        score). Toggle Follow on any trader, or <strong>add a wallet by address</strong>. Unfollowing an
        auto-followed trader mutes it so it won't be re-added.
      </P>
      <H3>Positioning</H3>
      <P>Net long vs short across your followed traders, by tradable symbol — a smart-money bias map for swing.</P>
      <H3>Settings</H3>
      <P>
        Auto-follow top-N, freshness threshold %, minimum account value, minimum book fraction, poll cadence, the
        swing bias toggle, minimum consensus for an alert, and alert routing (Dashboard / Telegram).
      </P>

      <H id="nse">Feeding NSE calls</H>
      <P>
        On the Signals tab, click <strong>+ NSE call</strong>, enter a source/channel name and paste the tip — e.g.
        <em> "BUY RELIANCE 2900 SL 2850 TGT 2980"</em>. It's parsed (action, symbol, entry, stop-loss, target),
        the symbol is matched to a tracked NSE instrument, and it appears in the feed (marked <strong>unverified</strong>).
        To automate, point a Telegram forwarding rule or a small script at <code>POST /copytrade/nse/ingest</code> with{' '}
        <code>{`{ source, text }`}</code>.
      </P>

      <H id="start">Getting started</H>
      <List dense sx={{ pl: 1 }}>
        <LI>Toggle the engine <strong>ON</strong> (top-right) and hit <strong>Scan now</strong> — it follows the top traders and syncs their books.</LI>
        <LI>Open <strong>Settings</strong>: set your freshness threshold and turn on Dashboard / Telegram alerts.</LI>
        <LI>Watch the <strong>Signals</strong> feed; on a fresh one, click <strong>Validate &amp; Trade on chart</strong>.</LI>
        <LI>(Optional) paste NSE tips via <strong>+ NSE call</strong>, or wire a forward rule to the ingest endpoint.</LI>
      </List>

      <GuideCallout kind="warning" title="Copy with judgement — not blindly">
        A trader being on a leaderboard is not a guarantee; even good traders take losses and use leverage you may
        not want. Always confirm the setup on the chart, size to your own risk, and mind the freshness gauge so you
        aren't chasing a move that already ran. NSE calls are unverified social tips — extra caution.
      </GuideCallout>

      <GuideExample title="A signal, end to end">
        Feed shows: <strong>LONG BTCUSD</strong> · trader "Q🌞 · #3" · entry 64,900 · now 64,980 (<em>better −0.1%</em>) ·
        18% of book · <em>2 traders</em> · entered 4m ago.<br />
        Click <strong>Validate &amp; Trade on chart</strong> → BTCUSD chart opens; you confirm structure/levels, then
        place a long on Delta at your size. Mark the signal <em>traded</em> to keep the feed clean.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Smart Money · copy signals', anchor: 'sources', body: 'smart money copy trading top trader hyperliquid on chain leaderboard follow signals delta' },
    { heading: 'Sources — Hyperliquid & NSE', anchor: 'sources', body: 'hyperliquid on chain positions verifiable okx binance masked nse unverified social telegram calls' },
    { heading: 'How it works — follow detect freshness consensus', anchor: 'how', body: 'follow quality score freshness gate chased consensus baseline fills validate trade' },
    { heading: 'Tabs — Signals / Traders / Positioning / Settings', anchor: 'tabs', body: 'signals feed traders discover follow positioning heatmap settings freshness poll cadence' },
    { heading: 'Feeding NSE calls', anchor: 'nse', body: 'nse paste call ingest webhook telegram forward buy sell stop loss target reliance' },
    { heading: 'Getting started with Smart Money', anchor: 'start', body: 'enable scan now follow alerts validate trade' },
  ],
};

export default Section_smart_money;
